import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import SyncAltRoundedIcon from "@mui/icons-material/SyncAltRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  cancelEmbeddedSignup,
  finalizeEmbeddedSignup,
  getEmbeddedSignupSession,
  startEmbeddedSignup,
} from "../../services/metaEmbeddedSignupApi";
import { isAllowedFacebookOrigin } from "../../utils/facebookSdk";

const SESSION_POLL_INTERVAL_MS = 1500;
// Only these end the flow; any other event (v5's are undocumented) lets the auth code finalize.
const META_EMBEDDED_SIGNUP_STOP_EVENTS = new Set([
  "CANCEL",
  "ERROR",
]);
const META_SESSION_EVENT_WAIT_TIMEOUT_MS = 1800;

function buildMetaEmbeddedSignupPopupUrl(sdkConfig, sessionKey) {
  const popupUrl = new URL(`https://www.facebook.com/${sdkConfig.graph_version}/dialog/oauth`);
  popupUrl.searchParams.set("display", "popup");
  popupUrl.searchParams.set("client_id", sdkConfig.app_id);
  popupUrl.searchParams.set("redirect_uri", sdkConfig.redirect_uri);
  popupUrl.searchParams.set("fallback_redirect_uri", sdkConfig.redirect_uri);
  popupUrl.searchParams.set("response_type", "code");
  popupUrl.searchParams.set("override_default_response_type", "true");
  popupUrl.searchParams.set("config_id", sdkConfig.configuration_id);
  popupUrl.searchParams.set("state", sessionKey);
  // v5 turns WhatsApp Business app onboarding (coexistence) on by default.
  popupUrl.searchParams.set("extras", JSON.stringify({
    version: sdkConfig.version,
  }));

  return popupUrl.toString();
}

function parseEmbeddedSignupMessage(rawData) {
  const payload = typeof rawData === "string" ? (() => {
    try {
      return JSON.parse(rawData);
    } catch (error) {
      return null;
    }
  })() : rawData;

  if (!payload || payload.type !== "WA_EMBEDDED_SIGNUP") {
    return null;
  }

  return payload;
}

function finalizeEventWithClientContext(sessionEvent, clientContext = {}) {
  const nextContext = Object.entries(clientContext).reduce((carry, [key, value]) => {
    if (value === null || value === undefined || value === "") {
      return carry;
    }

    carry[key] = value;
    return carry;
  }, {});

  if (Object.keys(nextContext).length === 0) {
    return sessionEvent;
  }

  return {
    ...sessionEvent,
    client_context: {
      ...(sessionEvent?.client_context ?? {}),
      ...nextContext,
    },
  };
}

function fallbackFinishEvent(extraContext = {}) {
  const {
    currentStep = "popup_callback",
    callbackTransport = "explicit_oauth_popup",
    transport = "explicit_oauth_popup",
    ...clientContext
  } = extraContext;

  return {
    type: "WA_EMBEDDED_SIGNUP",
    event: "FINISH",
    data: {
      current_step: currentStep,
      callback_transport: callbackTransport,
      session_logging_event_missing: true,
    },
    client_context: {
      transport,
      ...clientContext,
    },
  };
}

export default function MetaEmbeddedSignupDialog({
  open,
  onClose,
  allUsers,
  currentUser,
  isAdmin,
  onConnected,
}) {
  const [targetUserId, setTargetUserId] = useState("");
  const [launching, setLaunching] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("idle");

  const activeSessionRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const pollInFlightRef = useRef(false);
  const completionHandledRef = useRef(false);
  const callbackCodeHandledRef = useRef(false);
  const cancelEventHandledRef = useRef(false);
  const metaSessionEventRef = useRef(null);

  const availableUsers = useMemo(
    () => (isAdmin ? allUsers : [currentUser].filter(Boolean)),
    [allUsers, currentUser, isAdmin]
  );

  const isSecureOrigin = window.location.protocol === "https:";

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollInFlightRef.current = false;
  }, []);

  const resetFlowState = useCallback(() => {
    stopPolling();
    activeSessionRef.current = null;
    completionHandledRef.current = false;
    callbackCodeHandledRef.current = false;
    cancelEventHandledRef.current = false;
    metaSessionEventRef.current = null;
    setLaunching(false);
    setFinalizing(false);
    setResult(null);
    setError(null);
    setStatus("idle");
  }, [stopPolling]);

  useEffect(() => {
    if (!open) {
      resetFlowState();
      setTargetUserId(isAdmin ? "" : currentUser?.id ?? "");
    }
  }, [currentUser?.id, isAdmin, open, resetFlowState]);

  useEffect(() => () => {
    stopPolling();
  }, [stopPolling]);

  const applySessionSnapshot = useCallback((data) => {
    const nextSession = data?.session ?? null;
    const nextLine = data?.line ?? null;

    if (!nextSession) {
      return;
    }

    activeSessionRef.current = nextSession;

    if (nextSession.status === "completed") {
      stopPolling();
      setFinalizing(false);
      setError(null);
      setResult({
        session: nextSession,
        line: nextLine,
      });
      setStatus("success");

      if (!completionHandledRef.current) {
        completionHandledRef.current = true;
        onConnected?.(nextLine);
      }

      return;
    }

    if (nextSession.status === "failed") {
      stopPolling();
      setFinalizing(false);
      setStatus("error");
      setError(
        nextSession.last_error
        || "Meta Embedded Signup reached the server, but the connection could not be completed."
      );
      return;
    }

    if (nextSession.status === "cancelled") {
      stopPolling();
      setFinalizing(false);
      setStatus("cancelled");
      return;
    }

    if (nextSession.status === "authorizing" || nextSession.status === "token_exchanged") {
      setFinalizing(true);
      setStatus("finalizing");
      return;
    }

    setFinalizing(false);
    setStatus("popup");
  }, [onConnected, stopPolling]);

  const pollSessionStatus = useCallback(async () => {
    if (pollInFlightRef.current || !activeSessionRef.current?.session_key) {
      return;
    }

    pollInFlightRef.current = true;

    try {
      const data = await getEmbeddedSignupSession(activeSessionRef.current.session_key);
      applySessionSnapshot(data);
    } catch (requestError) {
      stopPolling();
      setFinalizing(false);
      setStatus("error");
      setError(
        requestError?.response?.data?.message
        || requestError?.message
        || "Unable to read the Meta Embedded Signup session status."
      );
    } finally {
      pollInFlightRef.current = false;
    }
  }, [applySessionSnapshot, stopPolling]);

  const waitForMetaSessionEvent = useCallback(async (timeoutMs = META_SESSION_EVENT_WAIT_TIMEOUT_MS) => {
    const startedAt = Date.now();

    while (Date.now() - startedAt < timeoutMs) {
      if (metaSessionEventRef.current) {
        return metaSessionEventRef.current;
      }

      await new Promise((resolve) => {
        window.setTimeout(resolve, 100);
      });
    }

    return metaSessionEventRef.current;
  }, []);

  const persistCancelledSession = useCallback(async (sessionEvent) => {
    if (!activeSessionRef.current?.session_key || cancelEventHandledRef.current) {
      return;
    }

    cancelEventHandledRef.current = true;
    stopPolling();
    setFinalizing(false);

    const errorMessage = sessionEvent?.data?.error_message || sessionEvent?.data?.error || "";
    const isError = Boolean(errorMessage) || String(sessionEvent?.event || "").toUpperCase() === "ERROR";

    setStatus(isError ? "error" : "cancelled");
    setError(isError ? errorMessage || "Meta reported an error before the signup could be completed." : null);

    try {
      await cancelEmbeddedSignup({
        session_key: activeSessionRef.current.session_key,
        session_event: sessionEvent,
      });
    } catch (requestError) {
      if (isError) {
        setError((currentError) => (
          currentError
          || requestError?.response?.data?.message
          || requestError?.message
          || "Meta reported an error before the signup could be completed."
        ));
      }
    }
  }, [stopPolling]);

  const finalizeFromAuthorizationCode = useCallback(async (payload, messageSource) => {
    if (!payload?.code || callbackCodeHandledRef.current) {
      return;
    }

    callbackCodeHandledRef.current = true;
    stopPolling();
    setError(null);
    setFinalizing(true);
    setStatus("finalizing");

    const notifyCallbackWindow = (resultPayload) => {
      try {
        messageSource?.postMessage(
          {
            type: "meta-embedded-signup-callback-result",
            ...resultPayload,
          },
          window.location.origin
        );
      } catch (error) {
        // The popup may have closed already. The original tab remains authoritative.
      }
    };

    try {
      const rawMetaSessionEvent = await waitForMetaSessionEvent();
      const metaEventName = String(rawMetaSessionEvent?.event || "").toUpperCase();

      if (rawMetaSessionEvent && META_EMBEDDED_SIGNUP_STOP_EVENTS.has(metaEventName)) {
        await persistCancelledSession(rawMetaSessionEvent);
        notifyCallbackWindow({
          status: metaEventName === "ERROR" ? "error" : "cancelled",
          message: rawMetaSessionEvent?.data?.error_message
            || "The Meta onboarding flow was cancelled before a WhatsApp line could be linked.",
        });
        return;
      }

      const sessionEvent = rawMetaSessionEvent
        ? finalizeEventWithClientContext(rawMetaSessionEvent, {
          transport: rawMetaSessionEvent?.client_context?.transport || "facebook_sdk_message",
          auth_code_transport: payload.codeSource || "facebook_sdk_callback",
          auth_code_received_at: new Date().toISOString(),
        })
        : fallbackFinishEvent({
          currentStep: payload.codeSource === "callback_page_message" ? "react_callback_page" : "popup_callback",
          callbackTransport: payload.codeSource === "callback_page_message" ? "react_route" : "explicit_oauth_popup",
          transport: payload.codeSource === "callback_page_message" ? "react_route_callback" : "explicit_oauth_popup",
          fallback_reason: "missing_meta_finish_event",
          auth_code_transport: payload.codeSource || "callback_page_message",
          auth_code_received_at: new Date().toISOString(),
        });

      const data = await finalizeEmbeddedSignup({
        session_key: payload.sessionKey,
        code: payload.code,
        session_event: sessionEvent,
      });

      notifyCallbackWindow({
        status: "success",
        message: "WhatsApp Business number connected.",
      });
      applySessionSnapshot(data);
    } catch (requestError) {
      const failureMessage =
        requestError?.response?.data?.message
        || requestError?.message
        || "The Meta popup returned to the app, but the signup callback could not be completed.";

      callbackCodeHandledRef.current = false;
      notifyCallbackWindow({
        status: "error",
        message: failureMessage,
      });
      setFinalizing(false);
      setStatus("error");
      setError(failureMessage);
    }
  }, [applySessionSnapshot, persistCancelledSession, stopPolling, waitForMetaSessionEvent]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (isAllowedFacebookOrigin(event.origin)) {
        const metaEvent = parseEmbeddedSignupMessage(event.data);

        if (metaEvent) {
          const nextMetaEvent = finalizeEventWithClientContext(metaEvent, {
            transport: "facebook_sdk_message",
            received_at: new Date().toISOString(),
            origin: event.origin,
          });

          metaSessionEventRef.current = nextMetaEvent;

          if (META_EMBEDDED_SIGNUP_STOP_EVENTS.has(String(nextMetaEvent.event || "").toUpperCase())) {
            persistCancelledSession(nextMetaEvent).catch(() => {});
          }
        }

        return;
      }

      const payload = event.data;

      if (!payload || payload.type !== "meta-embedded-signup-callback") {
        return;
      }

      if (payload.sessionKey !== activeSessionRef.current?.session_key) {
        return;
      }

      if (payload.status === "code_received") {
        finalizeFromAuthorizationCode({
          ...payload,
          codeSource: "callback_page_message",
        }, event.source);
        return;
      }

      if (payload.status === "success") {
        setError(null);
        setFinalizing(true);
        setStatus("finalizing");
        pollSessionStatus().catch(() => {});
        return;
      }

      if (payload.status === "cancelled") {
        stopPolling();
        setFinalizing(false);
        setStatus("cancelled");
        setError(null);
        return;
      }

      if (payload.status === "error") {
        stopPolling();
        setFinalizing(false);
        setStatus("error");
        setError(
          payload.message
          || "The Meta popup returned to the app, but the signup callback could not be completed."
        );
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [finalizeFromAuthorizationCode, persistCancelledSession, pollSessionStatus, stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollSessionStatus().catch(() => {});
    pollIntervalRef.current = window.setInterval(() => {
      pollSessionStatus().catch(() => {});
    }, SESSION_POLL_INTERVAL_MS);
  }, [pollSessionStatus, stopPolling]);

  const handleClose = () => {
    resetFlowState();
    onClose?.();
  };

  const handleLaunch = async () => {
    if (!isSecureOrigin) {
      setStatus("error");
      setError(
        "Meta Embedded Signup must be launched from an HTTPS admin/settings URL. Open this page from a secure HTTPS domain before trying again."
      );
      return;
    }

    setLaunching(true);
    setFinalizing(false);
    setResult(null);
    setError(null);
    setStatus("preparing");
    stopPolling();
    activeSessionRef.current = null;
    completionHandledRef.current = false;
    callbackCodeHandledRef.current = false;
    cancelEventHandledRef.current = false;
    metaSessionEventRef.current = null;

    try {
      const data = await startEmbeddedSignup({
        target_user_id: Number(targetUserId),
      });

      activeSessionRef.current = data.session;
      setStatus("popup");
      startPolling();

      const popupUrl = buildMetaEmbeddedSignupPopupUrl(data.sdk, data.session.session_key);
      const popup = window.open(
        popupUrl,
        "metaEmbeddedSignup",
        "popup=yes,width=720,height=820,menubar=no,toolbar=no,location=yes,resizable=yes,scrollbars=yes,status=no"
      );

      if (!popup) {
        throw new Error("The Meta popup was blocked. Allow popups for this site and try again.");
      }

      popup.focus?.();
    } catch (requestError) {
      stopPolling();
      setStatus("error");
      setError(
        requestError?.response?.data?.message
        || requestError?.message
        || "Unable to launch Meta Embedded Signup."
      );
    } finally {
      setLaunching(false);
    }
  };

  const selectedUser = availableUsers.find((entry) => Number(entry.id) === Number(targetUserId));

  return (
    <Dialog open={open} onClose={launching || finalizing ? undefined : handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>
        Connect existing WhatsApp Business number
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} pt={0.5}>
          <Alert severity="info" icon={<SyncAltRoundedIcon />}>
            This launches Meta Embedded Signup for coexistence. It keeps the same number on the consultant&apos;s phone and in Unibox.
          </Alert>

          {!isSecureOrigin ? (
            <Alert severity="warning">
              Meta Embedded Signup must be launched from an HTTPS admin/settings origin. Plain local HTTP URLs like `http://localhost:3000` are rejected by Meta for this flow. Use your secure staging/live domain or an HTTPS tunnel.
            </Alert>
          ) : null}

          <FormControl size="small" fullWidth disabled={!isAdmin}>
            <InputLabel>Consultant to link</InputLabel>
            <Select
              value={targetUserId}
              label="Consultant to link"
              onChange={(event) => setTargetUserId(event.target.value)}
            >
              {availableUsers.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedUser ? (
            <Alert severity="success" icon={<WhatsAppIcon />}>
              {selectedUser.name}
              {selectedUser.whatsapp_number
                ? ` currently has ${selectedUser.whatsapp_number} saved in the user record.`
                : " does not have a saved WhatsApp number yet, so the final line mapping will rely on the Meta onboarding result."}
            </Alert>
          ) : null}

          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              What this does
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.75}>
              Opens Meta Embedded Signup in a dedicated OAuth popup, keeps the redirect URI pinned to the server callback, and syncs the resulting WhatsApp Business account and phone number onto the consultant&apos;s Cloud API line.
            </Typography>
          </Box>

          {status !== "idle" ? (
            <Alert severity={status === "error" ? "error" : status === "success" ? "success" : status === "cancelled" ? "warning" : "info"}>
              {status === "preparing" ? "Preparing the Meta onboarding session." : null}
              {status === "popup" ? "Meta popup opened. Finish the existing-number onboarding steps there. This page will keep checking the server callback until the WhatsApp line is finalized." : null}
              {status === "waiting_callback" ? "The Meta popup closed, but this page is still waiting for the signup session details to settle. Keep this tab open while the session status is checked." : null}
              {status === "finalizing" ? "Meta returned the signup result. Finalizing the onboarding result and syncing the WhatsApp line now." : null}
              {status === "cancelled" ? "The Meta onboarding session was cancelled before completion." : null}
              {status === "success" ? "The existing WhatsApp Business number was linked for coexistence and saved into the WhatsApp Lines list." : null}
              {status === "error" && !error ? "Meta onboarding failed." : null}
            </Alert>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}

          {result?.line ? (
            <Alert severity="success" icon={<CloudDoneRoundedIcon />}>
              <Typography variant="body2" fontWeight={700}>
                Saved line: {result.line.display_name}
              </Typography>
              <Typography variant="caption" display="block">
                {result.line.phone_number || "No display phone number"} - Phone Number ID {result.line.cloud_api_phone_number_id}
              </Typography>
              <Typography variant="caption" display="block">
                Verification status: {result.session?.verification_status || result.line?.meta_onboarding?.verification_status || "Unknown"}
              </Typography>
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={launching || finalizing}>
          {status === "success" ? "Close" : "Cancel"}
        </Button>
        <Button
          variant="contained"
          onClick={handleLaunch}
          disabled={!targetUserId || launching || finalizing || status === "popup" || status === "waiting_callback"}
          startIcon={launching || finalizing ? <CircularProgress size={16} color="inherit" /> : <WhatsAppIcon />}
        >
          Connect existing WhatsApp Business number
        </Button>
      </DialogActions>
    </Dialog>
  );
}
